import moment from 'moment';
import { AccountData, OrderPageStatus, PassOut, PassStatus } from '../../api';
import type { PassScheduleSelectionDTO } from '../../api/models/PassScheduleSelectionDTO';
import type { PassOrderPageData } from '../../api/models/PassOrderPageData';
import { PassScheduleSelectionType } from '../../api/models/PassScheduleSelectionType';
import { PassType } from '../../api/models/PassType';

export const isFixedScheduleMonthly = (passType?: PassType): boolean => passType === PassType.FIXED_SCHEDULE_MONTHLY;

export const isRecurringMonthly = (passType?: PassType): boolean =>
  passType === PassType.UNLIMITED_MONTHLY || passType === PassType.FIXED_SCHEDULE_MONTHLY;

export const isMembership = (passOrderPage?: PassOrderPageData): boolean =>
  isRecurringMonthly(passOrderPage?.pass_type);

/**
 * Passes the account has hidden from the customer-facing lists. They are still served in the account
 * payload -- a direct link to a hidden pass has to keep working -- so every list the customer
 * browses has to filter them out itself.
 */
export const isPassHiddenFromCustomer = (account: AccountData | undefined, passOrderPageId: string): boolean =>
  (account?.pass_customer_portal_settings?.hidden_pass_order_page_ids ?? []).includes(passOrderPageId);

/**
 * Whether the customer can repurchase this pass straight from the portal, charging the card they
 * saved when they bought it. Recurring passes are excluded: they already bill the saved card every
 * cycle, so buying again would double-charge and leave two memberships running. Retired or portal-
 * hidden products are excluded for the same reason BuyPassPage filters them out of the buy list.
 */
/**
 * The newest pass the customer holds, by purchase date.
 */
export const getNewestPass = (passes: PassOut[]): PassOut | undefined =>
  [...passes].sort((a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf())[0];

export const canBuyPassAgain = (
  pass: PassOut,
  passOrderPage: PassOrderPageData | undefined,
  account: AccountData | undefined,
  passes: PassOut[],
): boolean => {
  if (account?.payment_integration_settings?.capabilities?.sales_tax_locked) {
    return false;
  }
  // Only the newest pass offers it: each repeat purchase carries the card token forward, so the
  // newest pass is the one holding the live token.
  if (getNewestPass(passes)?.id !== pass.id) {
    return false;
  }
  if (!pass.has_saved_card || !passOrderPage) {
    return false;
  }
  // A paused, cancelled or unpaid pass is not something to offer a repeat purchase from -- the
  // customer is mid-way through sorting that one out.
  if (
    pass.status === PassStatus.ARCHIVED ||
    pass.status === PassStatus.SUSPENDED ||
    pass.status === PassStatus.PENDING_PAYMENT
  ) {
    return false;
  }
  if (isRecurringMonthly(passOrderPage.pass_type) || passOrderPage.status !== OrderPageStatus.ACTIVE) {
    return false;
  }
  if (
    (passOrderPage.schedule_selection_type ?? PassScheduleSelectionType.NONE) !== PassScheduleSelectionType.NONE ||
    isFixedScheduleMonthly(passOrderPage.pass_type)
  ) {
    return false;
  }
  return !isPassHiddenFromCustomer(account, passOrderPage.id);
};

export const getPassOrderPage = (
  account: AccountData | undefined,
  orderPageId: string,
): PassOrderPageData | undefined => {
  return account?.pass_order_pages?.find((p) => p.id === orderPageId);
};

export const getPassOrderPageName = (
  passOrderPage: PassOrderPageData | undefined,
  language: string,
  t: (key: string) => string,
): string =>
  (passOrderPage?.label as Record<string, string> | undefined)?.[language] ||
  passOrderPage?.label?.en ||
  t('manage_passes.pass.unnamed');

export const getExpirationDate = (pass: PassOut, passOrderPage?: PassOrderPageData): string | null => {
  if (pass.expiration_date) return pass.expiration_date;
  if (passOrderPage?.validity_days && pass.created_at) {
    return moment(pass.created_at).add(passOrderPage.validity_days, 'days').toISOString();
  }
  return null;
};

export const getUsedPunches = (pass: PassOut): number => {
  return pass.redeemed_order_ids?.length || 0;
};

export const getEffectiveNumPunches = (pass: PassOut, passOrderPage?: PassOrderPageData): number | null => {
  return pass.num_punches_override ?? passOrderPage?.num_punches ?? null;
};

export const isPassExpired = (pass: PassOut, passOrderPage?: PassOrderPageData): boolean => {
  const expDate = getExpirationDate(pass, passOrderPage);
  if (!expDate) return false;
  return moment(expDate).isBefore(moment());
};

export const isPassExpiringSoon = (pass: PassOut, passOrderPage?: PassOrderPageData, days = 7): boolean => {
  const expDate = getExpirationDate(pass, passOrderPage);
  if (!expDate) return false;
  const daysUntilExpiry = moment(expDate).diff(moment(), 'days');
  return daysUntilExpiry >= 0 && daysUntilExpiry <= days;
};

export const getDaysUntilExpiry = (pass: PassOut, passOrderPage?: PassOrderPageData): number | null => {
  const expDate = getExpirationDate(pass, passOrderPage);
  if (!expDate) return null;
  return moment(expDate).diff(moment(), 'days');
};

export const isPassActive = (pass: PassOut, passOrderPage?: PassOrderPageData): boolean => {
  return pass.status === PassStatus.ACTIVE && !isPassExpired(pass, passOrderPage);
};

export const hasPassStarted = (pass: PassOut): boolean => {
  return !pass.start_date || moment(pass.start_date).isSameOrBefore(moment(), 'day');
};

export const hasPassStartedBy = (pass: PassOut, dateStr: string): boolean => {
  return !pass.start_date || moment(pass.start_date).isSameOrBefore(moment(dateStr), 'day');
};

/**
 * Whether the pass will still be valid on the session's date. A membership that keeps renewing
 * covers dates beyond its current cycle end; a cancelled membership or a punch pass ends at its
 * expiration date, so later sessions need a new pass.
 */
export const passCoversSessionDate = (
  pass: PassOut,
  passOrderPage: PassOrderPageData | undefined,
  dateStr: string,
): boolean => {
  if (isMembership(passOrderPage) && pass.auto_renew !== false) return true;
  const expirationDate = getExpirationDate(pass, passOrderPage);
  if (!expirationDate) return true;
  return !moment(dateStr).isAfter(moment(expirationDate), 'day');
};

export const isPassActiveForDate = (
  pass: PassOut,
  passOrderPage: PassOrderPageData | undefined,
  dateStr: string,
): boolean => {
  return (
    isPassActive(pass, passOrderPage) &&
    hasPassStartedBy(pass, dateStr) &&
    passCoversSessionDate(pass, passOrderPage, dateStr)
  );
};

export const getExperienceName = (account: AccountData, orderPageId: string, language: string): string => {
  for (const exp of account.experiences || []) {
    const orderPage = exp.order_pages?.find((op) => op.id === orderPageId);
    if (orderPage) {
      return (orderPage.label as Record<string, string>)?.[language] || orderPage.label?.en || '';
    }
  }
  return '';
};

export const getRedeemableOrderPageIds = (passes: PassOut[], account: AccountData): string[] => {
  const ids = new Set<string>();
  const allOrderPageIds = account.experiences?.flatMap((e) => e.order_pages.map((op) => op.id)) || [];

  for (const pass of passes) {
    const passOP = getPassOrderPage(account, pass.order_page_id);
    const isRelevant = pass.status === PassStatus.ACTIVE && !isPassExpired(pass, passOP);
    if (isRelevant) {
      const redeemableOn = passOP?.redeemable_on;
      if (!redeemableOn || redeemableOn.length === 0) {
        allOrderPageIds.forEach((id) => ids.add(id));
      } else {
        redeemableOn.forEach((item) => ids.add(item.order_page_id));
      }
    }
  }
  return Array.from(ids);
};

export enum MembershipState {
  RENEWING = 'renewing',
  ENDING = 'ending',
  PAUSED = 'paused',
  PAUSE_SCHEDULED = 'pause_scheduled',
  LAPSED = 'lapsed',
}

/**
 * Whether a pause has been booked but has not started yet. The server applies a scheduled pause on
 * its start date -- until then the pass stays active with `auto_renew` true, so the pause is only
 * visible through these two dates.
 */
export const hasScheduledPause = (pass: PassOut): boolean =>
  pass.status !== PassStatus.SUSPENDED &&
  !!pass.pause_start_date &&
  moment(pass.pause_start_date).isAfter(moment(), 'day');

/**
 * Where a membership stands with its direct debit. `next_charge_date` is the server's own answer to
 * "will this be charged again?" -- it comes back null for paused, ended and expired memberships.
 */
export const getMembershipState = (pass: PassOut): MembershipState => {
  if (pass.status === PassStatus.SUSPENDED) return MembershipState.PAUSED;
  if (hasScheduledPause(pass)) return MembershipState.PAUSE_SCHEDULED;
  if (pass.auto_renew === false) return MembershipState.ENDING;
  if (pass.next_charge_date) return MembershipState.RENEWING;
  return MembershipState.LAPSED;
};

/**
 * The last renewal attempt was declined. Dunning retries on `next_charge_date` and keeps access open
 * meanwhile, so the pass still reads as RENEWING -- the trailing payment record is the only signal.
 * `succeeded` defaults to true server-side, so only an explicit false counts.
 */
export const hasFailedLastCharge = (pass: PassOut): boolean =>
  pass.payments?.[pass.payments.length - 1]?.succeeded === false;

export const getPendingSwapTarget = (account: AccountData | undefined, pass: PassOut): PassOrderPageData | undefined =>
  pass.pending_swap ? getPassOrderPage(account, pass.pending_swap.target_pass_order_page_id) : undefined;

/**
 * A paused membership is not active, but it is still the customer's current one -- it belongs with
 * the live passes rather than in the past-passes list.
 */
export const isPassCurrent = (pass: PassOut, passOrderPage?: PassOrderPageData): boolean =>
  isPassActive(pass, passOrderPage) || (isMembership(passOrderPage) && pass.status === PassStatus.SUSPENDED);

/**
 * The weekly slots in force today. A swap does not delete the slots it replaces -- it closes them
 * with an (inclusive) `effective_until`, so the pass keeps carrying the schedule it used to have.
 */
export const getCurrentScheduleSelections = (pass: PassOut): PassScheduleSelectionDTO[] =>
  (pass.schedule_selections || []).filter(
    (sel) => !sel.effective_until || moment(sel.effective_until).isSameOrAfter(moment(), 'day'),
  );
